// ======================================================================
// File: src/modules/ticket/layout/TicketSidebar.jsx
// Purpose: Ticket Sidebar (collapsible) — now notifies parent on toggle
// Updated: 2025-12-29 23:58 IST
// Change: Added onToggle prop + useEffect to notify parent about expanded state
// ======================================================================

import React, { useEffect, useRef, useState, useContext } from "react";
import { NavLink, useNavigate } from "react-router-dom";

import {
  MdHomeFilled,
  MdDashboard,
  MdListAlt,
  MdAssignmentTurnedIn,
  MdMenu,
  MdChevronLeft,
} from "react-icons/md";

import { AuthContext } from "../../../context/AuthContext";
import "../../../components/sidebar.css";

const LS_KEY = "ticket_sidebar_collapsed";

export default function TicketSidebar({
  mobileOpen,
  setMobileOpen,
  onToggle, // NEW: callback to notify parent about expanded/collapsed
}) {
  const navigate = useNavigate();
  const { hasPermission } = useContext(AuthContext);

  const [collapsed, setCollapsed] = useState(
    localStorage.getItem(LS_KEY) === "true"
  );

  const [hoverExpand, setHoverExpand] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 992);
  const sidebarRef = useRef(null);

  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth <= 992);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  // Save sidebar collapse state to localStorage
  useEffect(() => {
    localStorage.setItem(LS_KEY, collapsed);
    // Notify parent layout about current expanded state
    const expanded = !collapsed && !isMobile;
    if (typeof onToggle === "function") onToggle(expanded);
  }, [collapsed, isMobile, onToggle]);

  const expanded = !collapsed || hoverExpand || (isMobile && mobileOpen === true);

  // -----------------------------------------
  // Ticket Menu Configuration
  // -----------------------------------------
  const menu = [
    {
      key: "back",
      label: "Back to Dashboard",
      icon: <MdHomeFilled size={22} />,
      to: "/dashboard",
      isPrimary: true,
    },
    {
      key: "ticket-dashboard",
      label: "Dashboard",
      to: "/tickets/dashboard",
      icon: <MdDashboard size={22} />,
      permission: "TICKET_VIEW:view",
    },

    // ✅ NEW: Ticket Performance Dashboard
    {
      key: "ticket-performance",
      label: "Performance",
      to: "/tickets/performance-dashboard",
      icon: <MdDashboard size={22} />,
      permission: "TICKET_PERFORMANCE_VIEW:view",
    },

    {
      key: "ticket-all",
      label: "All Tickets",
      to: "/tickets/all",
      icon: <MdListAlt size={22} />,
      permission: "TICKET_VIEW:view",
    },
    {
      key: "ticket-my",
      label: "My Tickets",
      to: "/tickets/my",
      icon: <MdAssignmentTurnedIn size={22} />,
      permission: "TICKET_VIEW:view",
    },
  ];

  const handleCollapseToggle = () => {
    if (isMobile) return setMobileOpen(false);
    setCollapsed((c) => !c);
  };

  const handleBackClick = () => {
    navigate("/dashboard");
    if (isMobile) setMobileOpen(false);
  };

  return (
    <>
      {/* Mobile overlay */}
      {isMobile && mobileOpen && (
        <div
          className="tf-sidebar-backdrop"
          onClick={() => setMobileOpen(false)}
        />
      )}

      <aside
        ref={sidebarRef}
        className={`tf-sidebar ${expanded ? "expanded" : "collapsed"} ${
          isMobile && mobileOpen ? "open" : ""
        }`}
        onMouseLeave={() => setHoverExpand(false)}
      >
        {/* Hover edge to expand */}
        <div
          className="tf-sidebar-hover-edge"
          onMouseEnter={() => collapsed && !isMobile && setHoverExpand(true)}
        />

        {/* Sidebar Header */}
        <div className="tf-sidebar-header">
          <div className="tf-logo">
            <span className="tf-logo-icon">TK</span>
            {expanded && <span className="tf-logo-text">Ticket</span>}
          </div>

          {/* Collapse button */}
          <button
            className="tf-icon-btn"
            onClick={handleCollapseToggle}
            onMouseEnter={() =>
              collapsed && !isMobile && setHoverExpand(true)
            }
            aria-label="Toggle sidebar"
          >
            {isMobile ? <MdMenu /> : <MdChevronLeft />}
          </button>
        </div>

        {/* Navigation List */}
        <nav className="tf-nav">
          {menu.map((m) => {
            if (m.permission) {
              const [func, action] = m.permission.split(":");
              if (!hasPermission(func, action)) return null;
            }

            // Primary Back Button
            if (m.isPrimary && !expanded) {
              return (
                <button
                  key={m.key}
                  className="tf-home-icon"
                  onClick={handleBackClick}
                  title={m.label}
                >
                  {m.icon}
                </button>
              );
            }

            if (m.isPrimary && expanded) {
              return (
                <button
                  key={m.key}
                  className="tf-back-primary"
                  onClick={handleBackClick}
                >
                  {m.icon}
                  <span className="tf-label">{m.label}</span>
                </button>
              );
            }

            // Standard Menu
            return (
              <NavLink
                key={m.key}
                to={m.to}
                className={({ isActive }) =>
                  `tf-nav-item ${isActive ? "active" : ""}`
                }
                title={!expanded ? m.label : ""}
                onClick={() => isMobile && setMobileOpen(false)}
              >
                <span className="tf-icon">{m.icon}</span>
                {expanded && <span className="tf-label">{m.label}</span>}
                <span className="tf-active-indicator" />
              </NavLink>
            );
          })}
        </nav>

        <div className="tf-admin-label">Admin</div>
      </aside>
    </>
  );
}
